import { Link } from 'react-router-dom';
import { useCompetition, useCompetitionIdParam } from '@/hooks/useCompetitions';
import { useAssignJudge, useJudgesForCompetition, useJudgesProgress, useRemoveJudge } from '@/hooks/useJudging';
import { useCategories } from '@/hooks/useCategories';
import { useUsersByIds } from '@/hooks/useUsers';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Table, type Column } from '@/components/ui/Table';
import { AssignUserForm } from '@/components/organizer/AssignUserForm';
import type { JudgeAssignment } from '@/api/judges';

/**
 * Per-competition judge assignment (GET/POST /judging/competitions/{id}/judges,
 * DELETE .../judges/{judgeId}) - judges stay organizer-invite-only (no
 * self-service signup, unlike competitors). Assigning by email creates the
 * account on the fly if it doesn't exist yet - see AssignUserForm.
 */
export function OrganizerJudgesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { competitionId } = useCompetitionIdParam();
  const competition = useCompetition(competitionId);
  const assignments = useJudgesForCompetition(competitionId);
  const assignJudge = useAssignJudge(competitionId);
  const removeJudge = useRemoveJudge(competitionId);
  const { usersById } = useUsersByIds((assignments.data?.items ?? []).map((a) => a.userId));
  const progress = useJudgesProgress(competitionId);
  const categories = useCategories(competitionId);
  const progressByUser = new Map((progress.data?.items ?? []).map((p) => [p.userId, p]));
  const categoryNames = new Map((categories.data?.items ?? []).map((c) => [c.id, c.name]));

  function describeUser(userId: string): string {
    const u = usersById.get(userId);
    if (!u) return userId;
    return u.displayName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;
  }

  const columns: Column<JudgeAssignment>[] = [
    { header: 'Sodnik', accessor: (a) => describeUser(a.userId) },
    { header: 'E-pošta', accessor: (a) => usersById.get(a.userId)?.email ?? '—' },
    {
      header: 'Ocenjeno',
      accessor: (a) => {
        const p = progressByUser.get(a.userId);
        if (!p) return progress.isError ? '—' : '…';
        if (p.total === 0) return <span className="text-sm text-fg-muted">Ni sprejetih prispevkov</span>;
        return (
          <div className="flex min-w-[10rem] flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="font-mono tabular-nums text-fg">
                {p.scored} / {p.total}
              </span>
              {p.scored === p.total && <span className="text-xs font-medium text-success">končano</span>}
            </div>
            <ProgressBar value={p.scored} max={p.total} />
          </div>
        );
      },
    },
    {
      header: 'Po kategorijah',
      accessor: (a) => {
        const p = progressByUser.get(a.userId);
        if (!p || p.categories.length === 0) return '—';
        return (
          <ul className="flex flex-col gap-0.5 text-xs text-fg-secondary">
            {p.categories.map((c) => (
              <li key={c.categoryId}>
                {categoryNames.get(c.categoryId) ?? 'Kategorija'}:{' '}
                <span className="font-mono tabular-nums">
                  {c.scored} / {c.total}
                </span>
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      header: 'Zadnja ocena',
      accessor: (a) => {
        const last = progressByUser.get(a.userId)?.lastScoredAt;
        return last ? new Date(last).toLocaleString() : '—';
      },
    },
    { header: 'Dodeljeno', accessor: (a) => new Date(a.assignedAt).toLocaleDateString() },
    {
      header: 'Dejanja',
      accessor: (a) => (
        <Button
          variant="danger"
          size="sm"
          isLoading={removeJudge.isPending && removeJudge.variables === a.id}
          onClick={() => {
            if (confirm(`Odstranite "${describeUser(a.userId)}" kot sodnika za to tekmovanje?`)) {
              removeJudge.mutate(a.id);
            }
          }}
        >
          Odstrani
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div>
        {!embedded && (
          <>
        <Link to="/organizer/competitions" className="text-sm text-fg-muted hover:underline">
          &larr; Nazaj na tekmovanja
        </Link>
        <h1 className="text-xl font-semibold text-fg">
          Sodniki {competition.data ? `za "${competition.data.name}"` : ''}
        </h1>
          </>
        )}
        <p className="text-sm text-fg-muted">
          Dodelite sodnika za ocenjevanje prispevkov na tem tekmovanju po e-pošti.
        </p>
      </div>

      <Card>
        <CardBody>
          <AssignUserForm
            actionLabel="Dodeli sodnika"
            isPending={assignJudge.isPending}
            error={assignJudge.error}
            onAssign={(payload) => assignJudge.mutateAsync(payload)}
          />
        </CardBody>
      </Card>

      {assignments.isLoading && <PageSpinner />}
      {assignments.isError && (
        <ErrorState message={getApiErrorMessage(assignments.error)} onRetry={() => assignments.refetch()} />
      )}
      {removeJudge.isError && <Alert variant="error">{getApiErrorMessage(removeJudge.error)}</Alert>}
      {assignments.isSuccess && assignments.data.items.length === 0 && (
        <EmptyState title="Še ni dodeljenih sodnikov" description="Zgoraj dodelite sodnika." />
      )}
      {assignments.isSuccess && assignments.data.items.length > 0 && (
        <Table columns={columns} rows={assignments.data.items} getRowKey={(a) => a.id} />
      )}
    </div>
  );
}
